/**
 * Session-scoped JSON fetch cache. The Today/Compatibility/Palmistry data files
 * are static and large, so we fetch each one at most once per session: concurrent
 * callers share the in-flight promise, and resolved values are kept so later reads
 * are synchronous (no refetch, no navigation flicker).
 */
const valueCache = new Map<string, unknown>()
const promiseCache = new Map<string, Promise<unknown>>()

/** Read an already-resolved cached value synchronously (e.g. to seed state). */
export function getCachedJson<T>(url: string): T | undefined {
  return valueCache.get(url) as T | undefined
}

/** Fetch + parse a JSON file once. Tolerates a stringified payload. */
export function fetchJsonCached<T>(url: string): Promise<T> {
  if (valueCache.has(url)) return Promise.resolve(valueCache.get(url) as T)

  const existing = promiseCache.get(url)
  if (existing) return existing as Promise<T>

  const promise = fetch(url)
    .then((res) => res.json())
    .then((raw: unknown) => {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw
      valueCache.set(url, data)
      promiseCache.delete(url)
      return data as T
    })
    .catch((err) => {
      promiseCache.delete(url) // allow a retry after a failed load
      throw err
    })

  promiseCache.set(url, promise)
  return promise
}

/** Fire-and-forget warm-up so the data is cached before it's navigated to. */
export function preloadJson(url: string): void {
  void fetchJsonCached(url).catch(() => {})
}
